<template>
  <div class="project-documents-view">
    <div class="header">
      <h1>
        Project Documents
        <span v-if="drawFilter" class="draw-badge">Draw {{ drawFilter }}</span>
      </h1>
      <div class="breadcrumb">
        <router-link to="/portfolio">Portfolio</router-link>
        <span> > </span>
        <router-link :to="`/portfolio/project-summary/${projectId}`">{{ projectName }}</router-link>
        <span> > Documents</span>
        <span v-if="drawFilter"> > Draw {{ drawFilter }}</span>
      </div>
    </div>

    <div class="controls">
      <button class="upload-btn" @click="showUploadModal = true">
        📤 Upload New Document
      </button>
      <div class="filters">
        <select v-model="statusFilter" class="filter-select">
          <option value="">All Statuses</option>
          <option value="not_started">Uploaded</option>
          <option value="queued">Queued</option>
          <option value="in_progress">Processing</option>
          <option value="done">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <select v-model="typeFilter" class="filter-select">
          <option value="">All Types</option>
          <option value="g702">G702</option>
          <option value="g703">G703</option>
          <option value="invoice">Invoice</option>
          <option value="lien_waiver">Lien Waiver</option>
        </select>
        <select v-model="drawFilter" class="filter-select">
          <option value="">All Draws</option>
          <option v-for="drawNum in availableDrawNumbers" :key="drawNum" :value="drawNum">
            Draw {{ drawNum }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading documents...</p>
    </div>

    <div v-else-if="error" class="error">
      <p>{{ error }}</p>
      <button @click="loadDocuments" class="retry-btn">Retry</button>
    </div>

    <div v-else class="documents-grid">
      <div v-if="filteredDocuments.length === 0" class="empty-state">
        <span class="empty-icon">📄</span>
        <h3>No documents found</h3>
        <p>Upload your first document to get started with OCR processing</p>
        <button class="upload-btn" @click="showUploadModal = true">
          📤 Upload Document
        </button>
      </div>

      <div v-for="document in filteredDocuments" :key="document.id" class="document-card">
        <div class="document-header">
          <div class="document-info">
            <h3 class="document-name">{{ getFileName(document.filepath) }}</h3>
            <span class="document-type" :class="document.template_type">
              {{ formatDocumentType(document.template_type) }}
            </span>
          </div>
          <div class="document-status" :class="document.status">
            <span class="status-icon">{{ getStatusIcon(document.status) }}</span>
            <span class="status-text">{{ formatStatus(document.status) }}</span>
          </div>
        </div>

        <div class="document-details">
          <div class="detail-row">
            <span class="label">Draw Number:</span>
            <span class="value">{{ document.draw_number || 'N/A' }}</span>
          </div>
          <div class="detail-row">
            <span class="label">Uploaded:</span>
            <span class="value">{{ formatDate(document.created_at) }}</span>
          </div>
          <div v-if="document.total_confidence" class="detail-row">
            <span class="label">Confidence:</span>
            <span class="value confidence" :class="getConfidenceClass(document.total_confidence)">
              {{ Math.round(document.total_confidence * 100) }}%
            </span>
          </div>
          <div v-if="document.error" class="detail-row error">
            <span class="label">Error:</span>
            <span class="value">{{ document.error }}</span>
          </div>
        </div>

        <div class="document-actions">
          <button 
            v-if="document.status === 'done'" 
            @click="viewDocument(document.id)"
            class="action-btn primary"
          >
            👁️ View Source & Results
          </button>
          
          <button 
            v-if="document.status === 'in_progress' || document.status === 'queued'" 
            @click="viewDocument(document.id)"
            class="action-btn secondary"
          >
            ⏳ View Processing Status
          </button>
          
          <button 
            v-if="document.status === 'failed'" 
            @click="retryDocument(document.id)"
            class="action-btn retry"
          >
            🔄 Retry Processing
          </button>

          <button 
            @click="downloadDocument(document.id)"
            class="action-btn secondary"
          >
            📥 Download Original
          </button>
          
          <button 
            @click="deleteDocument(document.id)"
            class="action-btn danger"
          >
            🗑️ Delete
          </button>
        </div>

        <!-- Processing Progress -->
        <div v-if="document.status === 'in_progress' || document.status === 'queued'" class="processing-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: getProgressWidth(document.status) }"></div>
          </div>
          <p class="progress-text">{{ getProgressText(document.status) }}</p>
        </div>
      </div>
    </div>

    <!-- Upload Modal (simplified version) -->
    <div v-if="showUploadModal" class="modal-backdrop" @click="showUploadModal = false">
      <div class="upload-modal" @click.stop>
        <div class="modal-header">
          <h3>Upload Document</h3>
          <button class="close-btn" @click="showUploadModal = false">×</button>
        </div>
        <div class="modal-body">
          <p>Document upload will be available here. For now, use the main project page to upload documents.</p>
        </div>
        <div class="modal-actions">
          <button @click="showUploadModal = false" class="btn secondary">Close</button>
          <button @click="goToProjectPage" class="btn primary">Go to Project Page</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { useAuthStore } from '@/stores/auth_store'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Reactive data
const projectId = ref(route.params.id)
const projectName = ref('Loading...')
const documents = ref([])
const loading = ref(true)
const error = ref('')
const statusFilter = ref('')
const typeFilter = ref('')
const drawFilter = ref(route.query.draw || '') // Add draw filter from query params
const showUploadModal = ref(false)

// API configuration
const apiBase = process.env.VUE_APP_ENV_API_BASE || "/api/v1/"
const authToken = ref({})

// Computed properties
const availableDrawNumbers = computed(() => {
  const drawNumbers = [...new Set(documents.value
    .map(doc => doc.draw_number)
    .filter(num => num != null)
  )].sort((a, b) => a - b)
  return drawNumbers
})

const filteredDocuments = computed(() => {
  return documents.value.filter(doc => {
    const statusMatch = !statusFilter.value || doc.status === statusFilter.value
    const typeMatch = !typeFilter.value || doc.template_type === typeFilter.value
    const drawMatch = !drawFilter.value || doc.draw_number == drawFilter.value
    return statusMatch && typeMatch && drawMatch
  })
})

// Methods
function getFileName(filepath) {
  if (!filepath) return 'Unknown File'
  return filepath.split('/').pop()
}

function formatDocumentType(type) {
  if (!type) return 'Unknown'
  return type.toUpperCase().replace('_', ' ')
}

function getStatusIcon(status) {
  const icons = {
    not_started: '📄',
    pending: '⏸️',
    queued: '⏳',
    in_progress: '🔄',
    done: '✅',
    failed: '❌'
  }
  return icons[status] || '❓'
}

function formatStatus(status) {
  const labels = {
    not_started: 'Uploaded',
    pending: 'Pending',
    queued: 'Queued',
    in_progress: 'Processing',
    done: 'Completed',
    failed: 'Failed'
  }
  return labels[status] || 'Unknown'
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function getConfidenceClass(confidence) {
  if (confidence >= 0.8) return 'high'
  if (confidence >= 0.6) return 'medium'
  return 'low'
}

function viewDocument(documentId) {
  // Open document viewer in new tab
  const viewerUrl = `/document-viewer/${documentId}`
  window.open(viewerUrl, '_blank')
}

function goToProjectPage() {
  router.push(`/portfolio/project-summary/${projectId.value}`)
}

async function downloadDocument(documentId) {
  try {
    const response = await axios.get(`${apiBase}documents/${documentId}/file`, {
      headers: authToken.value,
      responseType: 'blob'
    })
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    
    // Try to get filename from headers
    const contentDisposition = response.headers['content-disposition']
    let filename = 'document'
    if (contentDisposition) {
      const matches = /filename="(.+)"/.exec(contentDisposition)
      if (matches) filename = matches[1]
    }
    
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading document:', error)
    alert('Failed to download document')
  }
}

async function retryDocument(documentId) {
  try {
    await axios.post(`${apiBase}documents/${documentId}/retry`, {}, { headers: authToken.value })
    loadDocuments() // Refresh the list
  } catch (error) {
    console.error('Error retrying document:', error)
    alert('Failed to retry document processing')
  }
}

async function deleteDocument(documentId) {
  if (!confirm('Are you sure you want to delete this document?')) return
  
  try {
    await axios.delete(`${apiBase}documents/${documentId}`, { headers: authToken.value })
    loadDocuments() // Refresh the list
  } catch (error) {
    console.error('Error deleting document:', error)
    alert('Failed to delete document')
  }
}

async function loadDocuments() {
  try {
    loading.value = true
    error.value = ''
    
    // Load project info
    const projectResponse = await axios.get(`${apiBase}projects/${projectId.value}`, { headers: authToken.value })
    projectName.value = projectResponse.data.name
    
    // Load documents for this project
    const docsResponse = await axios.get(`${apiBase}documents?project_id=${projectId.value}`, { headers: authToken.value })
    documents.value = docsResponse.data
    
  } catch (err) {
    console.error('Error loading documents:', err)
    error.value = 'Failed to load documents'
  } finally {
    loading.value = false
  }
}

function getProgressWidth(status) {
  const progressMap = {
    not_started: '15%',
    queued: '25%',
    in_progress: '65%',
    done: '100%',
    failed: '0%'
  }
  return progressMap[status] || '50%'
}

function getProgressText(status) {
  const textMap = {
    not_started: 'Document uploaded, waiting for queue...',
    queued: 'Queued for OCR processing...',
    in_progress: 'OCR processing in progress...',
    done: 'Processing completed',
    failed: 'Processing failed'
  }
  return textMap[status] || 'Processing...'
}

// Lifecycle
onMounted(() => {
  authToken.value = authStore.getAuthTokenHeader
  loadDocuments()
})
</script>

<style scoped>
.project-documents-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header h1 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.breadcrumb {
  color: #666;
  margin-bottom: 30px;
}

.breadcrumb a {
  color: #3498db;
  text-decoration: none;
}

.draw-badge {
  background: #2ecc71;
  color: white;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 15px;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.upload-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}

.upload-btn:hover {
  background: #2980b9;
}

.filters {
  display: flex;
  gap: 15px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.loading, .error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.document-card {
  background: white;
  border: 1px solid #e1e8ed;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.document-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.document-name {
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 5px 0;
}

.document-type {
  background: #ecf0f1;
  color: #34495e;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.document-status {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
}

.document-status.done { color: #27ae60; }
.document-status.in_progress { color: #f39c12; }
.document-status.failed { color: #e74c3c; }
.document-status.pending { color: #95a5a6; }

.document-details {
  margin-bottom: 15px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 14px;
}

.label {
  color: #666;
  font-weight: 500;
}

.confidence.high { color: #27ae60; }
.confidence.medium { color: #f39c12; }
.confidence.low { color: #e74c3c; }

.document-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
}

.action-btn.primary {
  background: #3498db;
  color: white;
}

.action-btn.secondary {
  background: #ecf0f1;
  color: #34495e;
}

.action-btn.retry {
  background: #f39c12;
  color: white;
}

.action-btn.danger {
  background: #e74c3c;
  color: white;
}

.processing-progress {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #ecf0f1;
}

.progress-bar {
  width: 100%;
  height: 4px;
  background: #ecf0f1;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: #3498db;
  transition: width 0.3s ease;
}

.progress-text {
  margin-top: 5px;
  font-size: 12px;
  color: #666;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  grid-column: 1 / -1;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
  display: block;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.upload-modal {
  background: white;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
}

.modal-actions {
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.btn.primary {
  background: #3498db;
  color: white;
}

.btn.secondary {
  background: #ecf0f1;
  color: #34495e;
}
</style> 